import { Injectable, computed, inject, signal } from '@angular/core';

import {
  DEFAULT_LANG,
  Persistable,
  StoragePersistRule,
  StorageService,
  StoreSchema,
  createStorageKey,
} from '@gde-mit/angular-core';
import { DsThemeStore, ThemeMode } from '@gde-mit/angular-design-system';

import { patchState, signalState } from '@ngrx/signals';

export type LanguageMode = 'en' | 'hu' | 'system';

interface UiPreferencesStoreState {
  themeMode: ThemeMode;
  languageMode: LanguageMode;
  fueOnboardingOverride: boolean;
}

const STORE_KEY = 'UiPreferencesStore';
const storageKey = createStorageKey<UiPreferencesStoreState, typeof STORE_KEY>(STORE_KEY);

@Injectable({
  providedIn: 'root',
})
export class UiPreferencesStore extends Persistable implements StoreSchema {
  readonly #storageService = inject(StorageService);
  readonly #themeStore = inject(DsThemeStore);

  private static readonly FORCED_THEME_MODE: ThemeMode = 'light';

  readonly store = signalState<UiPreferencesStoreState>({
    themeMode: this.#readThemeMode(),
    languageMode: this.#readLanguageMode(),
    fueOnboardingOverride: this.#readFueOnboardingOverride(),
  });

  readonly persistedState = signal<StoragePersistRule[]>([
    { key: storageKey('themeMode'), value: (): ThemeMode => this.store.themeMode(), target: 'session' },
    { key: storageKey('languageMode'), value: (): LanguageMode => this.store.languageMode(), target: 'session' },
  ]);

  readonly dsTheme = computed(() => this.#themeStore.store.theme());
  readonly dsColorScheme = computed(() => this.#themeStore.store.colorScheme());
  readonly resolvedMode = computed<'dark' | 'light'>(() => {
    const forced = UiPreferencesStore.FORCED_THEME_MODE;
    if (forced === 'light' || forced === 'dark') {
      return forced;
    }

    const theme = this.dsTheme();
    if (theme === 'light' || theme === 'dark') {
      return theme;
    }
    return globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  constructor() {
    super();
    this.persistState(this.persistedState);
  }

  applyThemePreference(): void {
    this.#themeStore.setTheme(UiPreferencesStore.FORCED_THEME_MODE);
  }

  setThemeMode(mode: ThemeMode): void {
    patchState(this.store, { themeMode: mode });
    this.#themeStore.setTheme(UiPreferencesStore.FORCED_THEME_MODE);
  }

  setLanguageMode(mode: LanguageMode): void {
    // Language is locked to Hungarian for now, so `mode` is intentionally ignored; it is kept for forward compatibility.
    patchState(this.store, { languageMode: 'hu' });
  }

  resolveLanguage(mode: LanguageMode = this.store.languageMode()): 'en' | 'hu' {
    // Language is locked to Hungarian for now, so `mode` is intentionally ignored; it is kept for forward compatibility.
    return DEFAULT_LANG;
  }

  isFueOnboardingOverrideActive(): boolean {
    return this.store.fueOnboardingOverride();
  }

  syncOnboardingFromQueryParam(value: string | null): boolean {
    if (value === 'true') {
      return this.#setFueOnboardingOverride(true);
    }
    if (value === 'false') {
      return this.#setFueOnboardingOverride(false);
    }
    return false;
  }

  #setFueOnboardingOverride(enabled: boolean): boolean {
    const wasActive = this.store.fueOnboardingOverride();
    if (wasActive === enabled) {
      return false;
    }

    patchState(this.store, { fueOnboardingOverride: enabled });
    if (enabled) {
      this.#storageService.setItem(storageKey('fueOnboardingOverride'), true, 'local');
    } else {
      this.#storageService.removeItem(storageKey('fueOnboardingOverride'), 'local');
    }
    return true;
  }

  #readThemeMode(): ThemeMode {
    if (UiPreferencesStore.FORCED_THEME_MODE !== 'system') {
      return UiPreferencesStore.FORCED_THEME_MODE;
    }

    const raw = this.#safeGet(storageKey('themeMode'));
    return raw === 'system' || raw === 'light' || raw === 'dark' ? raw : 'system';
  }

  #readLanguageMode(): LanguageMode {
    return 'hu';
  }

  #readFueOnboardingOverride(): boolean {
    try {
      return this.#storageService.getItem<boolean>(storageKey('fueOnboardingOverride'), 'local') === true;
    } catch {
      return false;
    }
  }

  #safeGet(key: string): string | null {
    try {
      return this.#storageService.getItem<string>(key, 'session') ?? null;
    } catch {
      return null;
    }
  }
}
